"use client"

import styled from "@emotion/styled"
import { Pagination } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import Link from "next/link"
import { useCallback, useMemo, useRef, useState } from "react"
import { extractProperty, getNonUmbracoUrl, getUmbracoMediaUrl, PortArticleItem } from "utils"
import { useIsDesktop } from "./common/bit/MediaQueries"
import PortContainer from "./PortContainer"
import PortTitle from "./PortTitle"

const DESKTOP_PAGE_SIZE = 8
const MOBILE_PAGE_SIZE = 3

const StyledSection = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0;
  gap: 40px;
  background-color: ${(props) => props.theme.colors.white};
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 120px 0;
  }
`

const StyledItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;

  display: grid;
  gap: 24px;
  grid-template-columns: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 32px;
    grid-template-columns: 1fr 1fr;
    justify-content: center;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    grid-template-columns: repeat(4, 1fr);
  }
`

const StyledItem = styled.li`
  margin: 0;
  padding: 0;
  width: 100%;
  display: flex;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 325px;
  }
`

const StyledArticle = styled.article`
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.port.grey};
  background: white;
  width: 100%;
  overflow: hidden;
  padding-bottom: 24px;
`
const StyledArticleImage = styled.div`
  width: 100%;
  height: 175px;
  display: flex;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const StyledTextContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 16px 0;
`
const StyledDate = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
`
const StyledTitle = styled.h4`
  margin: 0;
  padding: 0;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%; /* 30px */
  letter-spacing: 0.6px;
  text-transform: capitalize;
`

const StyledDescription = styled.p`
  margin: 0;
  padding: 0;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`

const StyledLink = styled(Link)`
  padding: 0;
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 150%;
  color: inherit;
  text-decoration: underline;
  margin-inline: 16px;
`

const StyledPaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 12px 0 60px;

  font-family: var(--rag) !important;
  .MuiPagination-ul {
    gap: 16px;
  }

  .MuiPaginationItem-page {
    font-family: var(--rag);
    color: ${({ theme }) => theme.colors.port.defaultTextColor};
    border-radius: 50%;
    font-size: 16px;
    font-weight: 400;
    min-width: 24px;
    height: 24px;
    border: 1px solid ${({ theme }) => theme.colors.port.grey};
    margin: 0;
  }

  .MuiPaginationItem-page.Mui-selected {
    background-color: #0000fa;
    color: ${({ theme }) => theme.colors.white};
  }
`

type PortOurArticlesProps = {
  data: any
  articles?: PortArticleItem[]
  desktopPageSize?: number
  mobilePageSize?: number
}

const PortOurArticles = ({
  data,
  articles,
  desktopPageSize = DESKTOP_PAGE_SIZE,
  mobilePageSize = MOBILE_PAGE_SIZE,
}: PortOurArticlesProps) => {
  const t = useTranslations("PortOurArticles")
  const sectionRef = useRef<HTMLElement>(null)
  const isDesktop = useIsDesktop()
  const [page, setPage] = useState(1)

  const blackTitle = extractProperty(data, "blackTitle")
  const blueTitle = extractProperty(data, "blueTitle")

  const pageSize = isDesktop ? desktopPageSize : mobilePageSize

  const filteredArticles: PortArticleItem[] = useMemo(() => {
    const itemsList = extractProperty(data, "articles") || []
    return (
      itemsList
        ?.map((item: any) => articles?.find((article) => item.url === article.url))
        .filter(Boolean) || []
    )
  }, [articles, data])

  const totalPages = Math.ceil(filteredArticles.length / pageSize)

  const pagedArticles = useMemo(() => {
    const startIndex = (page - 1) * pageSize
    return filteredArticles.slice(startIndex, startIndex + pageSize)
  }, [filteredArticles, page, pageSize])

  const handlePageChange = useCallback((_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage)
    // Scroll to section top on page change
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [])

  // Reset to page 1 if current page exceeds total pages (e.g., on resize)
  useMemo(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(1)
    }
  }, [page, totalPages])

  // in case data fields are empty, return null
  if (!blackTitle && !blueTitle && filteredArticles.length === 0) {
    return null
  }

  return (
    <StyledSection id="port-our-articles" ref={sectionRef}>
      <PortContainer paddingInline={20}>
        <PortTitle normalText={blackTitle} highlightedText={blueTitle} centered />
      </PortContainer>
      {pagedArticles.length > 0 && (
        <PortContainer paddingInline={20}>
          <StyledItemList>
            {pagedArticles.map(({ url, date, description, image, title }, index) => (
              <StyledItem key={`${url}-${index}`}>
                <StyledArticle>
                  <StyledArticleImage>
                    {image && (
                      <Image src={getUmbracoMediaUrl(image)} alt={title} width={325} height={175} />
                    )}
                  </StyledArticleImage>
                  <StyledTextContent>
                    {date && <StyledDate>{date}</StyledDate>}
                    {title && <StyledTitle>{title}</StyledTitle>}
                    {description && <StyledDescription>{description}</StyledDescription>}
                  </StyledTextContent>
                  <StyledLink href={getNonUmbracoUrl(url)}>{t("readMore")}</StyledLink>
                </StyledArticle>
              </StyledItem>
            ))}
          </StyledItemList>
        </PortContainer>
      )}
      {totalPages > 1 && (
        <StyledPaginationContainer>
          <Pagination
            count={totalPages}
            page={page}
            color="primary"
            shape="rounded"
            size="small"
            onChange={handlePageChange}
            siblingCount={1}
            boundaryCount={1}
          />
        </StyledPaginationContainer>
      )}
    </StyledSection>
  )
}
export default PortOurArticles
